import Color from '@/Enums/Color.enum';
import ModalBody from '@/components/Modal/common/ModalBody';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import { handleApiValidationErrors } from '@/helpers/form';
import { useAppSelector } from '@/store/hooks';
import { Button, Modal } from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import UserAvatar from '../../Icons/UserAvatar';
import AccessManagement from '../Components/AccessManagement';
import AdditionalControls from '../Components/AdditionalControls';
import CampaignsApprovalSelect from '../Components/CampaignsApprovalSelect';
import UserInfo from '../Components/UserInfo';
import RemoveUserConfirmation from '../RemoveUserConfirmation';
import { getAllowedScreenKeys, isScreenAccessSubmitDisabled, updateUser } from '../data/userService';
import LoadingSkeleton from './LoadingSkeleton';
import PasswordResetConfirmation from './PasswordResetConfirmDialog';

// Pure gate extracted for testability (flank#4712): the form body — and
// AccessManagement's useWatch inside it — must not mount until `reset()`
// has actually applied `selectedUser`'s data, or it reads stale/default
// values on its first, synchronous render.
export const isFormReadyForUser = (resetAppliedForId, selectedUserId) => resetAppliedForId === selectedUserId;

const ViewProfileModal = ({
  open,
  handleClose,
  meRole,
  setLoading,
  formMethods,
  userRole,
  onModalClose,
  selectedUser,
  selectedUserLoading,
  screensByRole,
}) => {
  const {
    userProfile: { id: meId },
  } = useAppSelector(state => state.me);
  const { handleSubmit, watch, reset, setError, clearErrors } = formMethods;
  //#region modals
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);

  const openRemoveUserModal = () => setRemoveModalOpen(true);
  const onRemoveModalClose = () => setRemoveModalOpen(false);
  const onConfirmRemove = () => {
    onRemoveModalClose();
    handleClose(true);
  };

  const openPasswordResetModal = () => setPasswordResetModalOpen(true);
  const onPasswordResetModalClose = () => setPasswordResetModalOpen(false);
  //#endregion

  // Tracks which user's data `reset()` has applied. `reset()` runs in a
  // passive effect, strictly after the render that first mounts the form
  // body below — so AccessManagement's useWatch can compute its initial
  // value from stale/default data before reset() ever runs, hiding the
  // Unified Reporting sub-toggle panel for a user who already has overrides
  // saved (flank#4712). Holding the form body behind the skeleton until
  // reset() has applied this `selectedUser` means it never mounts against
  // stale data.
  const [resetAppliedForId, setResetAppliedForId] = useState();

  useEffect(() => {
    reset(selectedUser);
    setResetAppliedForId(selectedUser?.id);
  }, [reset, selectedUser]);

  const isFormReady = isFormReadyForUser(resetAppliedForId, selectedUser?.id);

  const onSubmit = async data => {
    setLoading(true);
    clearErrors();
    try {
      await updateUser(data, getAllowedScreenKeys(screensByRole, data.userRole));
      setLoading(false);

      handleClose(true);
      toast.success('User updated successfully');

      reset();
    } catch (error) {
      setLoading(false);
      if (axios.isAxiosError(error) && error.response?.data?.errors?.length) {
        handleApiValidationErrors(error.response.data.errors, setError);
      } else {
        toast.error('Something went wrong');
      }
    }
  };

  const userId = watch('id');

  const disableOwnControls = useMemo(() => userId === meId, [meId, userId]);

  // undefined means screensByRole hasn't loaded an entry for this role yet —
  // block submit rather than let getScreens fall through to "no filter".
  const allowedScreenKeys = getAllowedScreenKeys(screensByRole, userRole);

  return (
    <>
      <Modal open={open} onClose={onModalClose} aria-labelledby='modal-title'>
        <ModalBody
          sx={{
            width: '800px',
            maxWidth: '90vw',
          }}
        >
          <ModalTitleCard handleClose={onModalClose} titleIcon={<UserAvatar />} title='Edit Profile' />
          {selectedUserLoading || (selectedUser?.id && !isFormReady) ? (
            <LoadingSkeleton />
          ) : selectedUser?.id ? (
            // A failed fetch leaves `selectedUser` at DEFAULT_USER, so the form would
            // render the new-user defaults over an existing profile and offer a Save.
            <FormProvider {...formMethods}>
              <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
                <UserInfo meRole={meRole} disableRoleControl={disableOwnControls} />
                <AdditionalControls userRole={userRole} meRole={meRole} />
                <AccessManagement userRole={userRole} meRole={meRole} />
                <CampaignsApprovalSelect userRole={userRole} />
                <hr className='-mx-[32px] my-5' />
                <div className='mt-5 flex justify-between'>
                  <div>
                    <Button
                      variant='outlined'
                      sx={{
                        color: Color.AccentRed,
                        marginRight: '12px',
                      }}
                      onClick={openRemoveUserModal}
                      disabled={disableOwnControls}
                    >
                      Remove User
                    </Button>
                    <Button
                      variant='outlined'
                      sx={{
                        color: Color.AccentRed,
                      }}
                      onClick={openPasswordResetModal}
                      disabled={disableOwnControls}
                    >
                      Reset Password
                    </Button>
                  </div>
                  <div>
                    <Button
                      sx={{
                        marginRight: '12px',
                      }}
                      color='inherit'
                      variant='outlined'
                      onClick={onModalClose}
                      type='button'
                    >
                      Cancel
                    </Button>
                    <Button
                      color='primary'
                      variant='contained'
                      type='submit'
                      disabled={isScreenAccessSubmitDisabled(allowedScreenKeys)}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </form>
            </FormProvider>
          ) : (
            <div className='mt-6'>
              <p className='text-center'>We couldn&apos;t load this profile. Close this window and try again.</p>
              <div className='mt-5 flex justify-end'>
                <Button color='inherit' variant='outlined' onClick={onModalClose} type='button'>
                  Close
                </Button>
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>
      <RemoveUserConfirmation
        open={removeModalOpen}
        handleClose={onRemoveModalClose}
        onConfirm={onConfirmRemove}
        userId={userId}
      />
      <PasswordResetConfirmation
        open={passwordResetModalOpen}
        handleClose={onPasswordResetModalClose}
        onConfirm={onPasswordResetModalClose}
        userId={userId}
      />
    </>
  );
};

ViewProfileModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  meRole: PropTypes.number,
  setLoading: PropTypes.func,
  formMethods: PropTypes.object,
  userRole: PropTypes.number,
  onModalClose: PropTypes.func,
  selectedUser: PropTypes.object,
  selectedUserLoading: PropTypes.bool,
  screensByRole: PropTypes.object,
};

export default ViewProfileModal;
